/**
 * EquipmentServiceHistory component displays the service history of a given equipment entity in a modal dialog.
 *
 * - Fetches service history data for the provided entity using an API call.
 * - Groups and formats the history entries by their creation date.
 * - Displays service information, last service date, periodicity, service notes, and any file links.
 * - Automatically scrolls to the bottom of the history list when new data is loaded.
 * - Uses Bootstrap Modal for UI and supports closing via a callback.
 *
 * @component
 * @param {Object} props - Component properties.
 * @param {boolean} props.show - Controls the visibility of the modal.
 * @param {Object} props.entity - The equipment entity whose service history is displayed.
 * @param {Function} props.closeModal - Callback to close the modal.
 * @param {Object} props.isoData - Additional ISO data for the equipment.
 *
 * @returns {JSX.Element} The rendered EquipmentServiceHistory modal component.
 */
import { useEffect, useState, useRef } from 'react';
import { Api, fetchFile } from './interface';
import Modal from 'react-bootstrap/Modal';
import { ArrowRight } from 'react-bootstrap-icons';
import { shortDate } from './date';
import {
  ISO_SERVICE_FILE_LINKS_TYPE,
  SHAREPOINT_LINK_PREFIX,
} from './constants';

/**
 * EquipmentServiceHistory component displays the service history of a given equipment entity in a modal.
 * It fetches the service history data from an API, groups and formats it by creation date, and renders
 * the information in a user-friendly format, including service details, notes, and file links.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {Object} props.entity - The equipment entity object containing at least Entity_DBID and EntityName.
 * @param {boolean} props.show - Controls the visibility of the modal.
 * @param {Function} props.closeModal - Callback to close the modal.
 * @param {Object} [props.isoData] - Optional ISO data for fallback values.
 *
 * @returns {JSX.Element} The rendered EquipmentServiceHistory modal component.
 */
export default function EquipmentServiceHistory(props) {
  const [historyList, setHistoryList] = useState([]);
  const [formattedHistory, setFormattedHistory] = useState([]);
  const historyRef = useRef();

  useEffect(() => {
    if (props.entity && props.entity.Entity_DBID) {
      Api({
        sp: 'getEntityServiceHistory',
        json: { id: props.entity?.Entity_DBID },
      }).then((response) => {
        if (Array.isArray(response)) {
          setHistoryList(response);
          prepareHistory(response);
          historyScrollToBottom('auto');
        } else {
          setHistoryList([]);
        }
      });
    } else {
      setHistoryList([]);
    }
  }, [props.entity]);

  /**
   * @function prepareHistory
   * Groups the service history entries by their creation date (CreatedDtm).
   * For each entry in the history:
   *   - Converts the CreatedDtm to a local date string to use as a grouping key.
   *   - Aggregates all entries with the same date into a single object, merging their meta type values.
   *   - Stores the CreatedBy and CreatedDtm for each group.
   * The result is an object where each key is a date, and the value is an object containing all relevant
   * service history fields for that date. */
  const prepareHistory = async (history) => {
    // Group by distinct CreatedDtm
    const grouped = {};
    let unformattedHistory = history ?? historyList;
    for (const item of unformattedHistory) {
      const dateObj = new Date(item.CreatedDtm);
      const date = dateObj.toLocaleDateString();
      const dtm = `${date}`;

      const isoFileLinkTypes = Object.values(ISO_SERVICE_FILE_LINKS_TYPE).map(
        (type) => type.LABEL
      );
      if (!grouped[dtm]) {
        grouped[dtm] = {};
      }

      let entitymetavalue = item.EntityMetaValue;
      // if the meta type is a file link, fetch the file details
      // if it's a SharePoint link, display the link directly
      // otherwise, fetch the file info
      if (isoFileLinkTypes.includes(item.EntityMetaType)) {
        if (item.EntityMetaValue?.includes(SHAREPOINT_LINK_PREFIX)) {
          entitymetavalue = {
            finalUrl: item.EntityMetaValue,
            originalName: item.EntityMetaValue,
          };
        } else {
          entitymetavalue = await fetchFile(item.EntityMetaValue, 'document');
        }
      }

      grouped[dtm] = {
        ...grouped[dtm],
        [item.EntityMetaType]: entitymetavalue,
        CreatedBy: item.CreatedBy,
        CreatedDtm: item.CreatedDtm,
      };
    }

    setFormattedHistory(grouped);
  };

  /**
   * Scrolls the history list to the bottom to ensure the latest entry is visible.
   * @param {ScrollBehavior} behavior - The scroll behavior ('auto' or 'smooth').
   */
  const historyScrollToBottom = (behavior) => {
    setTimeout(() => {
      historyRef?.current?.scrollIntoView({ behavior: behavior });
    }, 0);
  };

  return (
    <Modal
      show={props.show}
      onHide={() => props.closeModal()}
      className="entity-modal"
      size="lg"
    >
      <Modal.Header closeButton="true">
        <Modal.Title>
          Service History Of {props.entity?.EntityName || 'Equipment'}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className="fieldList-container">
          {Object.keys(formattedHistory)?.length === 0 ? (
            <span>
              <i>No service record found for {props.entity?.EntityName}</i>
            </span>
          ) : (
            <>
              {Object.keys(formattedHistory).map((dtm) => {
                const fileLinkKeys = Object.values(ISO_SERVICE_FILE_LINKS_TYPE)
                  .map((type) => type.LABEL)
                  .filter((label) =>
                    Object.keys(formattedHistory[dtm]).includes(label)
                  );

                return (
                  <div key={dtm} className="service-history-entry">
                    <div className="service-history-separator">
                      <div className="d-flex mb-1">
                        <span className="history-field">
                          Service Information
                        </span>{' '}
                        <span className="history-value">
                          <ArrowRight color="blue" />
                          {formattedHistory[dtm]?.['Service Information'] ??
                            props.isoData?.ServiceInformation?.EntityMetaValue}
                        </span>
                      </div>

                      {formattedHistory[dtm]?.['Last Service Date'] ? (
                        <div className="d-flex mb-1">
                          <span className="history-field">
                            Last Service Date
                          </span>{' '}
                          <span className="history-value">
                            <ArrowRight color="blue" />
                            {shortDate(
                              formattedHistory[dtm]?.['Last Service Date']
                            )}
                          </span>
                        </div>
                      ) : null}
                      <div className="d-flex mb-1">
                        <span className="history-field">Periodicity</span>{' '}
                        <span className="history-value">
                          <ArrowRight color="blue" />
                          {formattedHistory[dtm]?.Periodicity ??
                            props.isoData?.Periodicity?.EntityMetaValue}
                        </span>
                      </div>
                      {formattedHistory[dtm]?.['Service Notes'] ? (
                        <div className="d-flex mb-1">
                          <span className="history-field">Service Notes</span>{' '}
                          <span className="history-value">
                            <ArrowRight color="blue" />
                            {formattedHistory[dtm]?.['Service Notes']}
                          </span>
                        </div>
                      ) : null}

                      {/* Render file link keys if any */}
                      {fileLinkKeys.length > 0 &&
                        fileLinkKeys.map((label) => (
                          <div className="d-flex mb-1" key={label}>
                            <span className="history-field">{label}</span>{' '}
                            <span className="history-value">
                              <div className="service-history-fileLink">
                                <ArrowRight color="blue" />
                                {/* Render as clickable link */}
                                <a
                                  href={formattedHistory[dtm][label]?.finalUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="truncate-url"
                                >
                                  {formattedHistory[dtm][label]?.originalName}
                                </a>
                              </div>
                            </span>
                          </div>
                        ))}
                    </div>
                    <div className="service-history-metadata">
                      <div className="service-timestamp">
                        {new Date(
                          formattedHistory[dtm]?.CreatedDtm
                        ).toLocaleString()}
                      </div>
                      <div className="service-createdby">
                        {formattedHistory[dtm]?.CreatedBy}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={historyRef}></div>
            </>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
}
